import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSwitcherMarkets } from '../config/markets';
import SEOHead from '../components/SEOHead';
import { trackMarketSelected } from '../utils/analytics';
import './MarketPickerPage.css';

const SAVED_MARKET_KEY = 'otc_preferred_market';

/**
 * MarketPickerPage — landing page at offtheclockspots.com/
 *
 * First visit: shows OTC branding + market dropdown with tooltip.
 * Return visit: auto-redirects to their saved market (skips this page).
 */
function MarketPickerPage() {
  const navigate = useNavigate();

  // Check for saved market preference — auto-redirect on return visits
  useEffect(() => {
    const savedSlug = localStorage.getItem(SAVED_MARKET_KEY);
    if (savedSlug) {
      navigate(`/${savedSlug}`, { replace: true });
    }
  }, [navigate]);

  // Get all OTC markets (use any OTC market ID to get the switcher group)
  const markets = getSwitcherMarkets('otc-naperville');

  const handleSelectMarket = (market) => {
    trackMarketSelected(market.id, market.regionLabel);
    localStorage.setItem(SAVED_MARKET_KEY, market.slug);
    navigate(`/${market.slug}`);
  };

  // If we're about to redirect, don't flash the picker
  const savedSlug = localStorage.getItem(SAVED_MARKET_KEY);
  if (savedSlug) {
    return (
      <div className="mp-loading">
        <div className="mp-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="mp-page">
      <SEOHead
        title="Choose Your Area"
        description="Find the best happy hour deals, daily specials, trivia, bingo, live music and local events near you. Pick your area to get started."
        path="/"
      />
      {/* Background gradient */}
      <div className="mp-bg" />

      <div className="mp-content">
        {/* Logo */}
        <div className="mp-logo-container">
          <img
            src="/images/otc-logo-picker.png"
            alt="Off the Clock Spots"
            className="mp-logo"
          />
        </div>

        {/* Tagline */}
        <p className="mp-tagline">
          Happy Hours, Daily Specials & Events near you
        </p>

        {/* Tooltip speech bubble */}
        <div className="mp-tooltip">
          <div className="mp-tooltip-arrow" />
          <p className="mp-tooltip-text">
            These are the areas currently available to you. Pick one to get started — you can change this anytime!
          </p>
        </div>

        {/* Market dropdown (always open) */}
        <div className="mp-dropdown">
          {markets.map(m => (
            <button
              key={m.id}
              className="mp-dropdown-item"
              onClick={() => handleSelectMarket(m)}
            >
              <span className="mp-dropdown-icon">📍</span>
              <span className="mp-dropdown-label">{m.regionLabel}</span>
              <span className="mp-dropdown-arrow">→</span>
            </button>
          ))}
        </div>

        {/* Footer note */}
        <p className="mp-footer-note">
          More areas coming soon!
        </p>
      </div>
    </div>
  );
}

export { SAVED_MARKET_KEY };
export default MarketPickerPage;
